/**
 * App — root component.
 *
 * Renders the header, tab navigation, and the active tab content.
 * Wraps the tree in the feathers ThemeProvider (light mode pinned so
 * Office desktop does not flip to dark).
 */

import React, { useState } from 'react';
import { ThemeProvider } from '@e14c/feathers';
import { Security } from '@e14c/feathers/icons';

import { TabNav, type ActiveTab } from './components/TabNav';
import { FactCheckTab } from './tabs/FactCheckTab';
import { WritingTab } from './tabs/WritingTab';
import { ReviewTab } from './tabs/ReviewTab';
import { ManageTab } from './tabs/ManageTab';
import { CriticTab } from './tabs/CriticTab';
import { useTaskPaneViewport } from './hooks/useTaskPaneViewport';

const AppInner: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('writing');
  useTaskPaneViewport();

  return (
    <div className="flex flex-col flex-1 h-0 min-h-0 overflow-hidden bg-background">
      {/* Header — brand yellow (#FFCD00 = bg-brand) */}
      <header className="flex items-center gap-2 px-3 py-2 bg-brand text-brand-foreground shrink-0">
        <Security size={20} />
        <span className="text-sm font-semibold">Edison Writing Assistant</span>
      </header>

      {/* Tab navigation */}
      <TabNav activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Tab body — keeps the height chain unbroken */}
      <div className="flex-1 h-0 min-h-0 overflow-hidden flex flex-col">
        {activeTab === 'factcheck' && <FactCheckTab />}
        {activeTab === 'review' && <ReviewTab />}
        {activeTab === 'manage' && <ManageTab />}
        {activeTab === 'writing' && <WritingTab />}
        {activeTab === 'critic' && <CriticTab />}
      </div>
    </div>
  );
};

/** ThemeProvider must wrap the tree so feathers components get the theme vars. */
const App: React.FC = () => (
  <ThemeProvider defaultTheme="light" storageKey="edison-addin-theme">
    <div className="flex flex-col flex-1 h-0 min-h-0 overflow-hidden">
      <AppInner />
    </div>
  </ThemeProvider>
);

export default App;
